<script setup lang="ts">
import { computed } from 'vue'
import type { Emits, Props } from './type'

defineOptions({
  name: 'AppErrorState',
})

const props = withDefaults(defineProps<Props>(), {
  message: '网络错误',
  description: '',
  variant: 'inline',
  retryable: true,
  retryLabel: '重试',
})

defineEmits<Emits>()

const illustrationSize = computed(() => (props.variant === 'full' ? 160 : 120))
</script>

<template>
  <section
    class="app-error-state"
    :class="`app-error-state--${variant}`"
    role="alert"
    aria-live="polite"
  >
    <div class="app-error-state__illustration" aria-hidden="true">
      <svg
        class="app-error-state__svg"
        :width="illustrationSize"
        :height="illustrationSize"
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
      >
        <path
          d="M104.352 194C93.9523 180.4 96.019 161.333 98.3523 153.5L146.852 151V194H104.352Z"
          fill="#FDFCFC"
        />
        <path
          d="M147.506 4.89258C148.151 4.96206 148.803 5.32751 149.397 5.61334C149.794 7.99548 149.686 14.6143 149.689 17.3048L149.636 40.7981L149.684 99.3282C149.673 109.366 149.895 120.147 149.63 130.121C161.6 133.044 162.6 149.246 158.568 158.687C156.912 162.562 153.669 165.179 149.759 166.593C149.515 169.16 149.739 172.65 149.728 175.305C149.726 180.809 149.687 186.319 149.723 191.822C149.742 194.867 147.956 196.419 145.895 193.456C145.772 190.012 145.85 186.057 145.84 182.58C145.801 177.333 145.805 172.085 145.852 166.839C140.064 166.263 135.989 163.375 133.691 157.98C132.37 159.303 130.777 160.323 129.021 160.968C125.368 162.292 121.677 161.926 118.186 160.301C115.308 158.962 111.723 155.934 110.629 152.907C108.537 153.817 101.253 155.875 98.9036 156.482C96.3775 168.4 99.6246 182.992 105.994 193.235C106.164 193.508 106.138 193.996 106.063 194.296C105.158 195.499 102.628 194.84 101.926 194.102C97.5275 189.479 95.3678 178.827 94.3962 173.278C93.7879 168.425 93.7603 162.343 94.5875 157.502C78.5633 161.814 57.8677 161.61 44.0906 151.493C20.4412 134.128 22.1991 96.647 38.0429 74.7715C36.5154 72.2716 35.5837 69.7833 34.9046 66.9422C31.7251 53.6403 37.5875 40.7391 48.1115 32.4733C55.8479 26.2756 65.759 23.4643 75.5974 24.6768C82.9444 25.5743 91.6055 29.3578 96.2128 35.2592C99.3159 34.0623 104.993 32.8645 108.311 32.3992C121.565 30.5409 133.038 31.5881 145.853 35.4241C146.166 25.8933 145.681 16.1422 146.045 6.60067C146.075 5.83127 146.931 5.28117 147.506 4.89258Z"
          fill="black"
        />
        <path
          d="M147.933 0.916016C149.078 1.0393 150.209 1.5469 150.774 1.82715L151.131 2.00879L153.001 2.9082L153.343 4.95605C153.602 6.51228 153.668 9.09075 153.69 11.3027C153.713 13.603 153.688 16.0383 153.689 17.2998V17.3135L153.636 40.8076L153.683 99.332C153.674 108.288 153.852 118.092 153.69 127.304C159.603 129.972 162.759 135.48 164.005 141.093C165.435 147.538 164.577 154.801 162.246 160.259C160.466 164.425 157.318 167.383 153.644 169.259C153.634 171.09 153.735 173.134 153.726 175.305L153.727 175.306C153.726 180.849 153.687 186.31 153.722 191.797C153.73 192.922 153.575 194.093 153.17 195.178C152.784 196.21 152.022 197.543 150.562 198.371C148.902 199.313 147.074 199.189 145.607 198.472C144.3 197.832 143.329 196.771 142.611 195.739L141.939 194.773L141.897 193.598C141.772 190.07 141.849 185.931 141.84 182.591C141.809 178.447 141.806 174.301 141.829 170.156C139.763 169.59 137.844 168.706 136.118 167.457C134.707 166.436 133.485 165.213 132.44 163.814C131.782 164.161 131.101 164.465 130.399 164.723L130.384 164.729C125.594 166.464 120.803 165.93 116.499 163.928C114.642 163.064 112.679 161.723 111.006 160.158C110.26 159.46 109.51 158.66 108.835 157.774C107.911 158.068 106.932 158.367 106 158.644C104.755 159.012 103.498 159.37 102.404 159.676C100.974 170.008 103.912 182.307 109.387 191.116L109.5 191.308C110.033 192.261 110.106 193.191 110.119 193.645C110.135 194.219 110.065 194.781 109.943 195.269L109.746 196.054L109.259 196.7C107.461 199.089 104.643 199.052 103.411 198.895C102.631 198.795 101.871 198.585 101.195 198.302C100.587 198.047 99.7374 197.604 99.0291 196.86L99.0282 196.859C96.1508 193.835 94.2938 189.247 93.0526 185.215C91.7667 181.038 90.9502 176.791 90.4559 173.968L90.4393 173.872L90.4276 173.775C90.0047 170.402 89.8417 166.461 90.0321 162.654C74.3089 165.701 55.2485 164.649 41.7235 154.718C28.6709 145.133 22.8289 130.13 22.2596 114.974C21.7249 100.738 25.8039 85.9674 33.3504 74.5264C32.2957 72.3731 31.5696 70.1944 31.0145 67.8721C27.4045 52.7692 34.1334 38.365 45.6405 29.3271C54.2126 22.4732 65.1868 19.365 76.0809 20.7061C83.2562 21.5826 91.8192 24.9612 97.4559 30.6406C100.583 29.7306 104.406 28.9483 107.08 28.5371L107.756 28.4375C119.849 26.7421 130.566 27.3695 141.953 30.2061C141.978 27.1893 141.953 24.1397 141.93 21.0469C141.893 16.2358 141.861 11.3231 142.048 6.44727C142.12 4.55967 143.19 3.31333 143.779 2.73926C144.379 2.1541 145.077 1.70598 145.264 1.5791L146.477 0.758789L147.933 0.916016ZM147.506 4.89258C146.931 5.28116 146.074 5.83123 146.045 6.60059C145.68 16.142 146.166 25.8932 145.852 35.4238C133.038 31.5878 121.565 30.5411 108.31 32.3994L107.662 32.4951C104.293 33.0144 99.1219 34.1367 96.2127 35.2588L95.7694 34.7119C91.2175 29.3042 83.2498 25.7794 76.2899 24.7695L75.5975 24.6768C65.7591 23.4643 55.8476 26.276 48.1112 32.4736C37.5873 40.7394 31.7246 53.6406 34.9041 66.9424C35.5832 69.7835 36.5153 72.2717 38.0428 74.7715C22.199 96.647 20.4412 134.127 44.0907 151.493C57.8677 161.61 78.5635 161.815 94.5877 157.503C93.7605 162.343 93.788 168.426 94.3963 173.278C95.3679 178.827 97.5272 189.479 101.926 194.103C102.606 194.817 105.001 195.458 105.973 194.403L106.062 194.296C106.128 194.033 106.157 193.627 106.049 193.347L105.994 193.235C99.6247 182.992 96.3772 168.4 98.9032 156.482C101.253 155.875 108.537 153.816 110.629 152.906C111.723 155.934 115.308 158.962 118.186 160.302C121.568 161.875 125.138 162.268 128.679 161.087L129.021 160.968C130.777 160.323 132.37 159.303 133.69 157.979C135.988 163.375 140.063 166.263 145.851 166.839C145.804 172.086 145.8 177.334 145.84 182.58C145.849 186.058 145.772 190.013 145.894 193.456C147.955 196.419 149.742 194.866 149.722 191.822C149.687 186.319 149.726 180.809 149.727 175.305C149.739 172.65 149.515 169.159 149.759 166.593C153.547 165.223 156.709 162.725 158.408 159.047L158.567 158.688C162.6 149.247 161.599 133.044 149.63 130.121C149.895 120.148 149.673 109.366 149.683 99.3281L149.636 40.7979L149.689 17.3047C149.686 14.7823 149.781 8.80714 149.466 6.10742L149.397 5.61328C148.803 5.32748 148.151 4.96212 147.506 4.89258Z"
          fill="white"
        />
        <path
          d="M70.9596 28.8629C77.1172 28.7693 83.918 30.8466 88.9198 34.5186C90.2416 35.4889 94.3799 39.917 94.7405 39.9105C96.8347 39.8726 100.946 38.3277 103.388 37.8318C117.039 35.0597 132.932 35.2667 146.014 40.3938C145.748 45.7304 145.912 50.7666 145.923 56.0644L145.893 76.4623C145.795 94.3307 145.832 112.199 146.006 130.067C136.443 131.479 137.888 136.16 131.807 139.251C122.342 144.062 112.13 147.86 101.983 150.898C97.0165 152.562 93.9755 153.134 88.8719 154.106C74.1587 156.911 59.3027 156.648 46.5991 147.721C37.8591 141.58 33.0383 131.398 31.1893 121.01C28.7435 107.267 32.4567 92.465 39.3322 80.4698C40.5176 78.4017 41.8751 76.9011 43.2039 75.0034C40.5458 69.7145 38.9586 67.5942 38.5113 61.2793C38.017 54.3049 41.1461 45.9557 45.7917 40.8205C52.9209 32.9399 60.4336 29.5138 70.9596 28.8629Z"
          fill="#FDFCFC"
        />
        <path
          d="M79.2426 102.005C81.9775 101.644 85.108 102.222 87.2612 103.977C94.0497 109.508 92.3679 119.175 85.7798 124.108C81.1483 127.576 76.6284 129.742 70.8912 130.444C68.5466 130.698 65.0778 129.532 63.234 128.155C58.3098 124.477 56.6797 117.364 60.6033 112.348C64.3922 107.504 73.2951 102.945 79.2426 102.005Z"
          fill="black"
        />
        <path
          d="M75.24 106.473C75.2832 106.564 75.3264 106.655 75.3694 106.747C73.1803 110.724 73.4432 115.272 76.4993 118.709C78.5639 121.031 80.7422 121.466 83.6102 121.59C80.5344 124.142 75.2612 126.169 71.3296 126.814C70.2676 126.945 69.1903 126.875 68.1544 126.607C66.0406 126.057 63.8103 124.556 62.7804 122.626C58.1288 113.913 69.8024 109.504 75.24 106.473Z"
          fill="#FDFCFC"
        />
        <path
          d="M130.317 78.1703C140.354 77.1697 145.08 89.8124 138.927 97.0002C134.04 102.708 128.126 105.298 120.842 106.281C110.677 106.632 105.872 95.7901 111.838 88.0651C115.39 83.4646 124.523 78.8349 130.317 78.1703Z"
          fill="black"
        />
        <path
          d="M125.15 82.9814C125.526 83.4691 124.982 83.7039 124.745 84.3341C121.879 91.9814 126.063 97.8243 134.005 97.7041C130.393 99.7538 126.781 101.774 122.668 102.621C120.95 102.823 119.561 103.016 117.858 102.497C114.407 101.454 111.272 96.9448 112.682 93.3377C114.65 88.3016 120.577 85.1671 125.15 82.9814Z"
          fill="#FDFCFC"
        />
        <path
          d="M116.93 69.9434C118.007 70.0191 119.401 71.2071 119.325 72.3863C118.538 74.3965 109.883 81.9772 107.978 83.0453C100.871 81.441 110.881 75.2435 112.487 73.2425C113.301 72.2273 115.733 70.5146 116.93 69.9434Z"
          fill="black"
        />
        <path
          d="M80.9226 93.2778C82.8024 92.9618 83.7346 94.9489 83.0241 96.0085C81.3838 98.4545 74.8488 101.217 72.2233 102.111C71.1425 101.831 69.2967 100.683 70.0815 99.5255C71.8457 96.923 78.1852 94.3372 80.9226 93.2778Z"
          fill="black"
        />
        <path
          d="M146.261 134.149C147.886 133.981 150.134 134.465 151.471 135.398C157.842 139.844 157.657 151.949 153.957 157.983C152.378 160.558 149.512 162.17 146.715 162.77C136.855 161.853 134.48 151.479 136.813 143.324C138.222 138.399 141.095 135.206 146.261 134.149Z"
          fill="#FDFCFC"
        />
        <path
          d="M132.016 144.064C132.4 144.341 132.27 144.132 132.202 144.692C131.853 147.601 131.589 150.775 132.296 153.635C127.328 157.756 123.457 158.915 117.79 154.94C116.546 153.831 115.482 152.652 114.131 151.628C120.496 149.145 125.816 146.7 132.016 144.064Z"
          fill="#7ADB8B"
        />
        <path
          d="M60.4629 174.865C61.1098 174.939 61.4283 174.917 61.876 175.382C62.4572 176.478 61.576 179.849 61.3643 181.234C61.0599 183.227 63.8587 187.622 63.6289 188.646C63.3263 189.167 62.6504 189.453 62.1036 189.749C57.4462 187.328 56.2044 178.343 60.4629 174.865ZM70.0489 175.584C70.6954 175.736 71.3536 176.132 71.7276 176.697C72.0483 177.696 71.5984 180.092 71.7002 181.384C71.925 184.227 75.6182 186.353 72.503 188.268C68.5858 188.186 66.7678 178.735 70.0489 175.584Z"
          fill="black"
        />
        <path
          d="M60.8033 171.885C60.8643 171.892 60.9227 171.897 60.9986 171.905C61.0672 171.912 61.1611 171.922 61.2564 171.934C61.4435 171.956 61.7371 171.998 62.0669 172.092C62.7632 172.291 63.3381 172.641 63.8306 173.099L64.0367 173.301L64.3287 173.604L64.5259 173.977C65.041 174.948 65.0758 176.008 65.0708 176.59C65.0649 177.287 64.979 178.014 64.8833 178.647C64.7858 179.294 64.663 179.935 64.5591 180.466C64.458 180.983 64.3864 181.345 64.3433 181.606C64.3513 181.657 64.3781 181.844 64.4908 182.214C64.6554 182.754 64.9131 183.394 65.2251 184.121C65.507 184.778 65.8764 185.602 66.1294 186.262C66.2597 186.601 66.4012 187.002 66.4996 187.407C66.5732 187.711 66.7444 188.463 66.5562 189.303L66.4546 189.755L66.2222 190.154C65.7143 191.028 64.9951 191.544 64.5582 191.819C64.1766 192.06 63.6091 192.345 63.5318 192.387L62.1324 193.146L60.7193 192.411C57.1885 190.576 55.389 186.603 54.9615 183.063C54.5325 179.512 55.3415 175.175 58.565 172.542L59.5455 171.74L60.8033 171.885ZM70.7359 172.664C71.9705 172.955 73.3529 173.718 74.2291 175.042L74.4576 175.387L74.5835 175.78C74.8051 176.47 74.8379 177.174 74.8433 177.634C74.8493 178.145 74.8192 178.679 74.7886 179.133C74.7161 180.209 74.6627 180.788 74.691 181.147H74.69C74.699 181.262 74.7568 181.503 75.0162 181.999C75.1435 182.242 75.285 182.485 75.4673 182.799C75.6343 183.086 75.8483 183.455 76.0376 183.834C76.3799 184.52 76.9379 185.791 76.6792 187.293C76.3919 188.962 75.2721 190.087 74.0738 190.823L73.3218 191.285L72.44 191.267C70.1449 191.219 68.5241 189.795 67.5562 188.428C66.5736 187.039 65.9534 185.319 65.6304 183.646C65.3039 181.954 65.2414 180.106 65.5406 178.373C65.8335 176.677 66.5264 174.807 67.9703 173.42L69.148 172.29L70.7359 172.664ZM60.4625 174.865C56.204 178.343 57.4458 187.328 62.1031 189.749C62.6499 189.453 63.3258 189.167 63.6285 188.646C63.8583 187.622 61.0594 183.227 61.3638 181.234C61.5756 179.849 62.4567 176.478 61.8755 175.382C61.4278 174.917 61.1093 174.939 60.4625 174.865ZM60.6675 188.819C60.6753 188.86 60.6795 188.864 60.6695 188.823C60.6692 188.822 60.6679 188.821 60.6675 188.819ZM70.0484 175.584C66.7674 178.735 68.5854 188.186 72.5025 188.268C75.6177 186.353 71.9245 184.227 71.6998 181.384C71.5979 180.092 72.0478 177.696 71.7271 176.697C71.3531 176.132 70.6949 175.736 70.0484 175.584Z"
          fill="white"
        />
      </svg>
    </div>

    <div class="app-error-state__copy">
      <p class="app-error-state__message">{{ message }}</p>
      <p v-if="description" class="app-error-state__description">{{ description }}</p>
    </div>

    <button v-if="retryable" type="button" class="app-error-state__action" @click="$emit('retry')">
      {{ retryLabel }}
    </button>
  </section>
</template>

<style scoped>
.app-error-state {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--app-text-main);
  text-align: center;
}

.app-error-state--inline {
  min-height: 280px;
  gap: 12px;
  padding: 32px 16px;
}

.app-error-state--full {
  flex: 1 1 auto;
  min-height: calc(100vh - 16px);
  gap: 16px;
  padding: 48px 16px;
}

.app-error-state__illustration {
  position: relative;
  display: inline-flex;
  width: fit-content;
  height: fit-content;
  align-items: center;
  justify-content: center;
  isolation: isolate;
}

.app-error-state__illustration::before {
  position: absolute;
  inset: 16%;
  z-index: -1;
  border-radius: 50%;
  background: radial-gradient(circle, var(--app-surface-strong) 0%, transparent 70%);
  content: '';
  opacity: 0.72;
}

.app-error-state__svg {
  display: block;
  flex: 0 0 auto;
}

.app-error-state__copy {
  display: flex;
  max-width: min(100%, 360px);
  flex-direction: column;
  gap: 6px;
}

.app-error-state__message,
.app-error-state__description {
  margin: 0;
}

.app-error-state__message {
  color: var(--app-text-main);
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

.app-error-state__description {
  color: var(--app-text-secondary);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.app-error-state__action {
  display: inline-flex;
  min-width: 96px;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  border: 0;
  border-radius: 999px;
  appearance: none;
  background: var(--app-text-main);
  color: var(--app-bg);
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  outline: none;
  transition:
    opacity var(--app-interaction-duration) var(--app-interaction-ease),
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.app-error-state__action:hover,
.app-error-state__action:focus-visible {
  opacity: 0.86;
}

.app-error-state__action:focus-visible {
  box-shadow:
    0 0 0 3px var(--app-bg),
    0 0 0 5px var(--app-text-main);
}

.app-error-state__action:active {
  transform: scale(var(--app-interaction-standard-active-scale));
}

:global(:root.dark) .app-error-state__illustration::before,
:global(:root[data-theme='dark']) .app-error-state__illustration::before {
  inset: 10%;
  background: radial-gradient(
    circle,
    rgb(255 255 255 / 0.2) 0%,
    rgb(255 255 255 / 0.1) 48%,
    transparent 72%
  );
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .app-error-state__action {
    transition: none;
  }
}
</style>
